import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Partnership } from '../Entities/Partnership';

/**
 * Hook customizado para gerenciar parcerias com cache e prefetch
 * @param {object} filters - Filtros de busca
 * @param {number} page - Página atual
 * @param {number} limit - Itens por página
 * @returns {object} - Dados, loading, error e funções de prefetch
 */
export function usePartnerships(filters = {}, page = 1, limit = 10) {
  const queryClient = useQueryClient();

  // Criar chave única baseada nos filtros e página
  const queryKey = ['partnerships', filters, page, limit];

  // Query principal (usa configuração global do main.jsx)
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await Partnership.list('-created_at', filters, page, limit);
      return response;
    },
    // ✅ Configurações herdadas de main.jsx (global):
    // - staleTime: 30s (evita requisições excessivas)
    // - refetchOnMount: false (Socket.IO atualiza)
    // - refetchOnWindowFocus: false (Socket.IO atualiza)
    // - keepPreviousData: true (UX suave)
  });

  // Prefetch da próxima página
  const prefetchNextPage = () => {
    if (query.data && page < query.data.totalPages) {
      const nextPage = page + 1;
      const nextQueryKey = ['partnerships', filters, nextPage, limit];
      
      queryClient.prefetchQuery({
        queryKey: nextQueryKey,
        queryFn: async () => {
          return await Partnership.list('-created_at', filters, nextPage, limit);
        },
        // Usa configuração global (staleTime: 0)
      });
    }
  };

  // Prefetch da página anterior
  const prefetchPreviousPage = () => {
    if (page > 1) {
      const prevPage = page - 1;
      const prevQueryKey = ['partnerships', filters, prevPage, limit];
      
      queryClient.prefetchQuery({
        queryKey: prevQueryKey,
        queryFn: async () => {
          return await Partnership.list('-created_at', filters, prevPage, limit);
        },
        // Usa configuração global (staleTime: 0)
      });
    }
  };

  // Invalidar cache (útil após criar/editar/deletar)
  const invalidateCache = () => {
    queryClient.invalidateQueries(['partnerships']);
  };

  return {
    data: query.data?.data || [],
    total: query.data?.total || 0,
    totalPages: query.data?.totalPages || 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    prefetchNextPage,
    prefetchPreviousPage,
    invalidateCache,
  };
}

export default usePartnerships;
